/**
 * Hook para obtener datos de Home usando Tanstack Query
 * Reemplaza el hook anterior que usaba Apollo Client
 */

import { useQuery } from '@tanstack/react-query'
import { useMemo } from 'react'
import { queriesApi } from '../services/queriesService'
import normalizeText from '../utils/normalizeText'
import { NO_EXECUTE_CONTENTFULL_ROUTES } from '../utils/utils'
import type { HomeResponse } from '../types/contentful.types'

// Configuración de cache para Home (5 días)
const HOME_CACHE_TIME = 5 * 24 * 60 * 60 * 1000 // 5 días en milisegundos
const HOME_STALE_TIME = 5 * 24 * 60 * 60 * 1000 // 5 días en milisegundos

// Tipos para el estado procesado
interface HomeState {
	alliance: any
	careCenters: any
	careStore: any
	companyBenefits: any
	completeData: any
	easilyAccess: any
	isModalCareCenter: any
	isPremiumHomeContent: {
		noSubscription: any
		noSura: any
		noIPS: any
		noComfama: any
	}
	kutaiContent: any
	ourLives: any
	pac: any
	searchContent: any
	servicesPremium: any
	sliderAds: any
	sliderIntro: any
	smartfitPlatforms: any
	welcome: any
}

interface HomeHookResult extends HomeState {
	loading: boolean
	error: Error | null
	refetch: () => void
	isStale: boolean
	isRefetching: boolean
	cacheInfo: {
		cacheExpiry: Date
		lastUpdated: Date
	}
	rawData: HomeResponse | undefined
	contentFullData: any[]
}

/**
 * Hook para obtener y procesar datos de Home
 * @returns {HomeHookResult} Objeto con datos procesados y estados del hook
 */
export const useHome = (): HomeHookResult => {
	// Verificar si estamos en una ruta de excepción
	const pathname = typeof window !== 'undefined' ? window.location.pathname : ''
	const isExceptionRoute = NO_EXECUTE_CONTENTFULL_ROUTES.includes(pathname)

	// Query con Tanstack Query
	const {
		data: rawData,
		isLoading,
		error,
		refetch,
		isStale,
		isRefetching
	} = useQuery({
		queryKey: ['contentful', 'home'] as const,
		queryFn: queriesApi.getHome,
		staleTime: HOME_STALE_TIME,
		cacheTime: HOME_CACHE_TIME,
		retry: 2,
		retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 30000),
		refetchOnWindowFocus: false,
		refetchOnReconnect: true,
		enabled: !isExceptionRoute // Solo ejecutar si no estamos en ruta de excepción
	})

	// Manejar errores de manera moderna
	if (error) {
		console.error('Error al obtener datos de Home desde Contentful:', error)
	}

	// Procesar datos usando memoización para evitar recálculos innecesarios
	const processedData = useMemo(() => {
		if (!rawData || isLoading || error || isExceptionRoute) {
			return {
				alliance: false,
				careCenters: false,
				careStore: false,
				companyBenefits: false,
				completeData: false,
				easilyAccess: false,
				isModalCareCenter: false,
				isPremiumHomeContent: {
					noSubscription: false,
					noSura: false,
					noIPS: false,
					noComfama: false
				},
				kutaiContent: false,
				ourLives: false,
				pac: false,
				searchContent: false,
				servicesPremium: false,
				sliderAds: false,
				sliderIntro: false,
				smartfitPlatforms: false,
				welcome: false
			}
		}

		try {
			const data =
				rawData?.homeCollection?.items?.[0]?.vistas?.contenidoHomeBienvenidaCollection?.items

			if (!data || data.length === 0) {
				return {
					alliance: false,
					careCenters: false,
					careStore: false,
					companyBenefits: false,
					completeData: false,
					easilyAccess: false,
					isModalCareCenter: false,
					isPremiumHomeContent: {
						noSubscription: false,
						noSura: false,
						noIPS: false,
						noComfama: false
					},
					kutaiContent: false,
					ourLives: false,
					pac: false,
					searchContent: false,
					servicesPremium: false,
					sliderAds: false,
					sliderIntro: false,
					smartfitPlatforms: false,
					welcome: false
				}
			}

			// Para la bienvenida y los 4 cards
			const sliderAds = data?.filter(e => normalizeText(e?.idReto) === normalizeText('sliderAds'))

			// Para la bienvenida y los 4 cards
			const newWelcome = data?.filter(e => normalizeText(e?.idReto) === normalizeText('Bienvenida'))

			// Para la sección de Accede fácilmente
			const access = data?.filter(
				e => normalizeText(e?.idReto) === normalizeText('accedeFacilmente')
			)

			// Para la sección de Beneficios empresas
			const companyBenefits = data?.filter(
				e => normalizeText(e?.idReto) === normalizeText('beneficiosEmpresas')
			)

			// Para la sección de Centros del Cuidado
			const centers = data?.filter(
				e => normalizeText(e?.idReto) === normalizeText('Centro del Cuidado')
			)

			// Para la sección premiun
			const premium = data?.filter(
				e => normalizeText(e?.idReto) === normalizeText('serviciosPremium')
			)

			// Para la sección de Nuestras clases
			const lives = data?.filter(
				e => normalizeText(e?.idReto) === normalizeText('conoceNuestrasClases')
			)

			// Plan de atención complementaria del Home
			const pac = data?.filter(e => normalizeText(e?.idReto) === normalizeText('pacHome'))

			// Tienda del cuidado
			const careStore = data?.filter(e => normalizeText(e?.idReto) === normalizeText('careStore'))

			const livesVisible = lives && {
				...lives[0],
				beneficioCollection: {
					items: lives?.[0]?.beneficioCollection?.items?.filter(e => e.isVisible) || []
				}
			}

			// search
			const search = data?.filter(
				e => normalizeText(e?.idReto) === normalizeText('resultadosDeBusqueda')
			)

			// Para la seccion de Completa tus datos
			const complete = data?.filter(
				e => normalizeText(e?.idReto) === normalizeText('completaTusDatos')
			)

			const noSuscription = data?.filter(
				e => normalizeText(e?.idReto) === normalizeText('noSuscription')
			)[0]
			const noSura = data?.filter(e => normalizeText(e?.idReto) === normalizeText('noSura'))?.[0]
			const noIPS = data?.filter(e => normalizeText(e?.idReto) === normalizeText('noIPS'))?.[0]
			const noComfama = data?.filter(e => normalizeText(e.idReto) === normalizeText('Comfama'))[0]

			// alliance
			const allianceData = data?.filter(
				e => normalizeText(e.idReto) === normalizeText('aliadosRotador')
			)

			// alliance
			const sliderIntro = data?.filter(
				e => normalizeText(e.idReto) === normalizeText('queEsSaludContinua')
			)

			// smartfitPlatforms
			const smartfitPlatforms = data?.filter(
				e => normalizeText(e?.idReto) === normalizeText('smartfitPlatforms')
			)

			return {
				sliderAds: sliderAds?.[0] || false,
				welcome: newWelcome?.[0] || false,
				easilyAccess: access?.[0] || false,
				companyBenefits: companyBenefits?.[0] || false,
				careCenters: centers?.[0] || false,
				servicesPremium: premium?.[0] || false,
				ourLives: livesVisible || false,
				completeData: complete?.[0] || false,
				isModalCareCenter:
					data?.filter(e => normalizeText(e?.idReto) === normalizeText('modalCentrosCuidado'))[0] ||
					false,
				isPremiumHomeContent: {
					noSubscription: noSuscription,
					noSura,
					noIPS,
					noComfama
				},
				kutaiContent:
					data?.filter(e => normalizeText(e?.idReto) === normalizeText('flujoKutai'))?.[0] || false,
				alliance: allianceData?.[0] || false,
				searchContent: search?.[0] || false,
				pac: pac?.[0] || false,
				careStore: careStore?.[0] || false,
				sliderIntro: sliderIntro?.[0] || false,
				smartfitPlatforms: smartfitPlatforms?.[0] || false
			}
		} catch (transformError) {
			console.error('Error al procesar datos de Home:', transformError)
			return {
				alliance: false,
				careCenters: false,
				careStore: false,
				companyBenefits: false,
				completeData: false,
				easilyAccess: false,
				isModalCareCenter: false,
				isPremiumHomeContent: {
					noSubscription: false,
					noSura: false,
					noIPS: false,
					noComfama: false
				},
				kutaiContent: false,
				ourLives: false,
				pac: false,
				searchContent: false,
				servicesPremium: false,
				sliderAds: false,
				sliderIntro: false,
				smartfitPlatforms: false,
				welcome: false
			}
		}
	}, [rawData, isLoading, error, isExceptionRoute])

	// Datos completos para compatibilidad
	const contentFullData = useMemo(() => {
		if (!rawData || isLoading || error || isExceptionRoute) {
			return []
		}
		return (
			rawData?.homeCollection?.items?.[0]?.vistas?.contenidoHomeBienvenidaCollection?.items || []
		)
	}, [rawData, isLoading, error, isExceptionRoute])

	// Información adicional sobre el estado del cache
	const cacheInfo = useMemo(
		() => ({
			cacheExpiry: new Date(Date.now() + HOME_CACHE_TIME),
			lastUpdated: rawData ? new Date() : new Date()
		}),
		[rawData]
	)

	return {
		// Datos procesados
		...processedData,

		// Estados de la query
		loading: isLoading,
		error: error as Error | null,

		// Funcionalidades adicionales de Tanstack Query
		refetch,
		isStale,
		isRefetching,
		cacheInfo,

		// Datos raw por si se necesitan
		rawData,
		contentFullData
	}
}

/**
 * Hook para obtener datos de Home con opciones personalizadas
 * @param options - Opciones de configuración
 * @returns {Object} Objeto con loading, error, data y funciones de refetch
 */
export const useHomeWithOptions = (options: {
	enabled?: boolean
	staleTime?: number
	cacheTime?: number
	retry?: number
}) => {
	return useQuery({
		queryKey: ['contentful', 'home'],
		queryFn: queriesApi.getHome,
		staleTime: options.staleTime ?? HOME_STALE_TIME,
		cacheTime: options.cacheTime ?? HOME_CACHE_TIME,
		retry: options.retry ?? 2,
		retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 30000),
		refetchOnWindowFocus: false,
		refetchOnReconnect: true,
		enabled: options.enabled ?? true
	})
}

export default useHome
